// Organization > Dealers: every dealer, flat, with the regions it serves.
//
// This is also the only place a dealer's regions can be changed after it is
// created — a dealer can serve more than one, and dealers move.

import { useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import { useTenantOrganization } from "../../lib/api";
import { dealerRows, filterDealers, indexOrg } from "../../lib/organization";
import { useDealerEditor } from "./useOrgEditors";
import { FieldError, FieldLabel, inputClass } from "./primitives";
import {
  Cell,
  ConfirmDelete,
  EmptyState,
  ErrorState,
  FilterSelect,
  FormDialog,
  Loading,
  PageIntro,
  SearchBox,
  Table,
  Toolbar,
} from "./OrgKit";

export default function Dealers({ tenantId, readOnly = false }) {
  const { data, isLoading, isError, error, refetch } = useTenantOrganization(tenantId);
  const [filters, setFilters] = useState({ query: "", region: "all" });

  const ix = useMemo(() => indexOrg(data), [data]);
  const {
    open,
    setOpen,
    editing,
    form,
    setForm,
    errors,
    openAdd,
    openEdit,
    toggleRegion,
    submit,
    pending,
    deleting,
    setDeleting,
    askDelete,
    confirmDelete,
  } = useDealerEditor(tenantId, ix);
  const rows = useMemo(() => dealerRows(ix), [ix]);
  const shown = useMemo(() => filterDealers(rows, filters), [rows, filters]);

  if (isLoading) return <Loading />;
  if (isError) return <ErrorState error={error} onRetry={refetch} />;

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <>
      <PageIntro
        title="Dealers"
        actionLabel={readOnly ? null : "Add dealer"}
        onAction={openAdd}
      />

      {rows.length > 0 && (
        <Toolbar count={`${shown.length} of ${rows.length}`}>
          <SearchBox
            value={filters.query}
            onChange={(v) => set({ query: v })}
            placeholder="Search dealers"
            data-testid="dealer-search"
          />
          <FilterSelect
            value={filters.region}
            onChange={(v) => set({ region: v })}
            options={[
              { value: "all", label: "Any region" },
              ...ix.regions.map((r) => ({ value: r.name, label: r.name })),
            ]}
            aria-label="Region"
          />
        </Toolbar>
      )}

      {rows.length === 0 ? (
        <EmptyState
          title="No dealers yet"
          body="Add the first one."
          actionLabel={readOnly ? null : "Add dealer"}
          onAction={openAdd}
        />
      ) : shown.length === 0 ? (
        <EmptyState title="Nothing matches those filters" body="Clear a filter, or add the dealer that should be here." />
      ) : (
        <Table columns={["Dealer", "Code", "Regions", "Workshops", "People"]}>
          {shown.map((row) => (
            <tr key={row.id} data-testid="dealer-row">
              <Cell>
                <div className="text-[14px] font-medium text-[#111827]">{row.name}</div>
              </Cell>
              <Cell>{row.code}</Cell>
              <Cell>
                {row.regions || <span className="text-[#B45309]">Not in any region</span>}
              </Cell>
              <Cell>{row.workshops}</Cell>
              <Cell>{row.people}</Cell>
              <Cell className="whitespace-nowrap text-right">
                {!readOnly && (
                  <>
                <button
                  type="button"
                  onClick={() => openEdit(row)}
                  aria-label={`Edit ${row.name}`}
                  className="mr-1 inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#111827]"
                >
                  <Pencil size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => askDelete(row.id)}
                  aria-label={`Delete ${row.name}`}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] hover:bg-[#FEF2F2] hover:text-[#DC2626]"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
                  </svg>
                </button>
                  </>
                )}
              </Cell>
            </tr>
          ))}
        </Table>
      )}

      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? `Edit ${editing.name}` : "Add dealer"}
        onSubmit={submit}
        submitLabel={editing ? "Save changes" : "Add dealer"}
        pending={pending}
      >
        <div>
          <FieldLabel>Dealer name</FieldLabel>
          <input
            type="text"
            className={inputClass}
            value={form.name}
            placeholder="Kataria Motors"
            autoFocus
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <FieldError>{errors.name}</FieldError>
        </div>
        <div>
          <FieldLabel>Dealer code</FieldLabel>
          <input
            type="text"
            className={inputClass}
            value={form.dealer_code}
            placeholder="KM-01"
            onChange={(e) => setForm({ ...form, dealer_code: e.target.value })}
          />
          <FieldError>{errors.dealer_code}</FieldError>
        </div>
        <div>
          <FieldLabel>Regions</FieldLabel>
          {ix.regions.length ? (
            <div className="flex max-h-[200px] flex-col gap-1 overflow-y-auto rounded-xl bg-[#F9FAFB] p-2">
              {ix.regions.map((r) => (
                <label
                  key={r.id}
                  className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-[14px] text-[#111827] hover:bg-white"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded accent-[#2667B5]"
                    checked={form.region_ids.includes(r.id)}
                    onChange={() => toggleRegion(r.id)}
                  />
                  {r.name}
                </label>
              ))}
            </div>
          ) : (
            <p className="text-[13px] text-[#6B7280]">
              No regions yet. A dealer can be added without one and placed later.
            </p>
          )}
        </div>
      </FormDialog>

      <ConfirmDelete
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        name={deleting?.name || ""}
        body="This cannot be undone."
        blocked={deleting?.blocked}
        onConfirm={confirmDelete}
        pending={pending}
      />
    </>
  );
}
